import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/platform/jsonschemas/common/jsonContributionRegistry';
import { workbenchColorsSchemaId } from 'vscode/vscode/vs/platform/theme/common/colorUtils';
import 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { tokenStylingSchemaId } from 'vscode/vscode/vs/platform/theme/common/tokenClassificationRegistry';

const _moduleId = "vs/workbench/services/themes/common/colorThemeSchema";
const textMateScopes = [
    'comment',
    'comment.block',
    'comment.block.documentation',
    'comment.line',
    'constant',
    'constant.character',
    'constant.character.escape',
    'constant.numeric',
    'constant.numeric.integer',
    'constant.numeric.float',
    'constant.numeric.hex',
    'constant.numeric.octal',
    'constant.other',
    'constant.regexp',
    'constant.rgb-value',
    'emphasis',
    'entity',
    'entity.name',
    'entity.name.class',
    'entity.name.function',
    'entity.name.method',
    'entity.name.section',
    'entity.name.selector',
    'entity.name.tag',
    'entity.name.type',
    'entity.other',
    'entity.other.attribute-name',
    'entity.other.inherited-class',
    'invalid',
    'invalid.deprecated',
    'invalid.illegal',
    'keyword',
    'keyword.control',
    'keyword.operator',
    'keyword.operator.new',
    'keyword.operator.assignment',
    'keyword.operator.arithmetic',
    'keyword.operator.logical',
    'keyword.other',
    'markup',
    'markup.bold',
    'markup.changed',
    'markup.deleted',
    'markup.heading',
    'markup.inline.raw',
    'markup.inserted',
    'markup.italic',
    'markup.list',
    'markup.list.numbered',
    'markup.list.unnumbered',
    'markup.other',
    'markup.quote',
    'markup.raw',
    'markup.underline',
    'markup.underline.link',
    'meta',
    'meta.block',
    'meta.cast',
    'meta.class',
    'meta.function',
    'meta.function-call',
    'meta.preprocessor',
    'meta.return-type',
    'meta.selector',
    'meta.tag',
    'meta.type.annotation',
    'meta.type',
    'punctuation.definition.string.begin',
    'punctuation.definition.string.end',
    'punctuation.separator',
    'punctuation.separator.continuation',
    'punctuation.terminator',
    'storage',
    'storage.modifier',
    'storage.type',
    'string',
    'string.interpolated',
    'string.other',
    'string.quoted',
    'string.quoted.double',
    'string.quoted.other',
    'string.quoted.single',
    'string.quoted.triple',
    'string.regexp',
    'string.unquoted',
    'strong',
    'support',
    'support.class',
    'support.constant',
    'support.function',
    'support.other',
    'support.type',
    'support.type.property-name',
    'support.variable',
    'variable',
    'variable.language',
    'variable.name',
    'variable.other',
    'variable.other.readwrite',
    'variable.parameter'
];
const textmateColorsSchemaId = 'vscode://schemas/textmate-colors';
const textmateColorGroupSchemaId = `${textmateColorsSchemaId}#/definitions/colorGroup`;
const textmateColorSchema = {
    type: 'array',
    definitions: {
        colorGroup: {
            default: '#FF0000',
            anyOf: [
                {
                    type: 'string',
                    format: 'color-hex'
                },
                {
                    $ref: '#/definitions/settings'
                }
            ]
        },
        settings: {
            type: 'object',
            description: ( localizeWithPath(_moduleId, 0, 'Colors and styles for the token.')),
            properties: {
                foreground: {
                    type: 'string',
                    description: ( localizeWithPath(_moduleId, 1, 'Foreground color for the token.')),
                    format: 'color-hex',
                    default: '#ff0000'
                },
                background: {
                    type: 'string',
                    deprecationMessage: ( localizeWithPath(_moduleId, 2, 'Token background colors are currently not supported.'))
                },
                fontStyle: {
                    type: 'string',
                    description: ( localizeWithPath(
                        _moduleId,
                        3,
                        'Font style of the rule: \'italic\', \'bold\', \'underline\', \'strikethrough\' or a combination. The empty string unsets inherited settings.'
                    )),
                    pattern: '^(\\s*\\b(italic|bold|underline|strikethrough))*\\s*$',
                    patternErrorMessage: ( localizeWithPath(
                        _moduleId,
                        4,
                        'Font style must be \'italic\', \'bold\', \'underline\', \'strikethrough\' or a combination or the empty string.'
                    )),
                    defaultSnippets: [
                        { label: ( localizeWithPath(_moduleId, 5, 'None (clear inherited style)')), bodyText: '""' },
                        { body: 'italic' },
                        { body: 'bold' },
                        { body: 'underline' },
                        { body: 'strikethrough' },
                        { body: 'italic bold' },
                        { body: 'italic underline' },
                        { body: 'italic strikethrough' },
                        { body: 'bold underline' },
                        { body: 'bold strikethrough' },
                        { body: 'underline strikethrough' },
                        { body: 'italic bold underline' },
                        { body: 'italic bold strikethrough' },
                        { body: 'italic underline strikethrough' },
                        { body: 'bold underline strikethrough' },
                        { body: 'italic bold underline strikethrough' }
                    ]
                }
            },
            additionalProperties: false,
            defaultSnippets: [{ body: { foreground: '${1:#FF0000}', fontStyle: '${2:bold}' } }]
        }
    },
    items: {
        type: 'object',
        defaultSnippets: [{ body: { scope: '${1:keyword.operator}', settings: { foreground: '${2:#FF0000}' } } }],
        properties: {
            name: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 6, 'Description of the rule.'))
            },
            scope: {
                description: ( localizeWithPath(_moduleId, 7, 'Scope selector against which this rule matches.')),
                anyOf: [
                    {
                        enum: textMateScopes
                    },
                    {
                        type: 'string'
                    },
                    {
                        type: 'array',
                        items: {
                            enum: textMateScopes
                        }
                    },
                    {
                        type: 'array',
                        items: {
                            type: 'string'
                        }
                    }
                ]
            },
            settings: {
                $ref: '#/definitions/settings'
            }
        },
        required: [
            'settings'
        ],
        additionalProperties: false
    }
};
const colorThemeSchemaId = 'vscode://schemas/color-theme';
const colorThemeSchema = {
    type: 'object',
    allowComments: true,
    allowTrailingCommas: true,
    properties: {
        colors: {
            description: ( localizeWithPath(_moduleId, 8, 'Colors in the workbench')),
            $ref: workbenchColorsSchemaId,
            additionalProperties: false
        },
        tokenColors: {
            anyOf: [{
                    type: 'string',
                    description: ( localizeWithPath(_moduleId, 9, 'Path to a tmTheme file (relative to the current file).'))
                },
                {
                    description: ( localizeWithPath(_moduleId, 10, 'Colors for syntax highlighting')),
                    $ref: textmateColorsSchemaId
                }
            ]
        },
        semanticHighlighting: {
            type: 'boolean',
            description: ( localizeWithPath(
                _moduleId,
                11,
                'Whether semantic highlighting should be enabled for this theme.'
            ))
        },
        semanticTokenColors: {
            type: 'object',
            description: ( localizeWithPath(_moduleId, 12, 'Colors for semantic tokens')),
            $ref: tokenStylingSchemaId
        }
    }
};
function registerColorThemeSchemas() {
    const schemaRegistry = ( (Registry.as(Extensions.JSONContribution)));
    schemaRegistry.registerSchema(colorThemeSchemaId, colorThemeSchema);
    schemaRegistry.registerSchema(textmateColorsSchemaId, textmateColorSchema);
}

export { colorThemeSchemaId, registerColorThemeSchemas, textmateColorGroupSchemaId, textmateColorsSchemaId };
